import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
    DownloadOutlined,
    FilePdfOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { styled } from '../../../../../shared/Theme/theme';
import Loader from '../../../../../shared/SmallComponents/Loader';
import { TextInput } from '../../../../../shared/inputs/base';
import { businessPartner, settings as bpSettings } from '../../../../../../models/entities/businessPartner/businessPartner';
import {
    bpAccountingResponse,
    bpLedgerEntry
} from '../../../../../../models/entities/businessPartner/accounting';
import { settings as shipmentSettings } from '../../../../../../models/entities/shipment/shipment';
import { company } from '../../../../../../models/entities/company/company';
import { localSDK as client } from '../../../../../../sdk';
import { RootState } from '../../../../../../state/store/store';
import { singleCompanySelectors } from '../../../../../../state/ducks/singleCompany/selectors';
import { shipmentsSelectors } from '../../../../../../state/ducks/shipments/selectors';
import {
    buildBpInvoiceIssuer,
    paymentTermsLabelFromCode,
    resolveShipmentDisplayNumber
} from './invoicePdfTypes';
import { getBpInvoicePdfFile, getBpStatementPdfFile } from './invoicePdfUtils';
import { resolveBillingLinesForInvoice } from '../../../../commonSubEntities/accounting/invoiceLineTracking';
import { formatDate, formatMoney } from './format';
import { billingLine } from '../../../../../../models/entities/common_subentities/billingLine';

type Props = {
    businessPartner: businessPartner;
    settings?: bpSettings;
    issuerCompany?: company;
    shipmentSettings?: shipmentSettings;
};

type DocumentKindFilter = 'ALL' | 'INVOICE' | 'CREDIT_NOTE' | 'STATEMENT';

type FinancialDocumentKind = 'INVOICE' | 'CREDIT_NOTE' | 'STATEMENT';

type BpInvoiceListItem = {
    id: string;
    shipmentId: string;
    shipmentNumber: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string | null;
    currency: string;
    subtotal: number;
    sentAt: string | null;
};

type FinancialDocumentItem = {
    id: string;
    kind: FinancialDocumentKind;
    title: string;
    subtitle: string;
    date: string;
    amount?: number;
    currency?: string;
    shipmentId?: string;
    shipmentNumber?: string;
    invoiceId?: string;
    billingLineId?: number;
};

const FILTER_TABS: Array<{ value: DocumentKindFilter; label: string }> = [
    { value: 'ALL', label: 'All Documents' },
    { value: 'INVOICE', label: 'Invoices' },
    { value: 'CREDIT_NOTE', label: 'Credit Notes' },
    { value: 'STATEMENT', label: 'Statements' }
];

const STATEMENT_DOC_ID = 'statement-current';

const buildCreditNoteTitle = (entry: bpLedgerEntry) =>
    entry.documentNumber || `CRN-${entry.shipmentNumber || entry.shipmentId.slice(0, 8)}`;

const FinancialDocumentsTab = ({ businessPartner, issuerCompany, shipmentSettings }: Props) => {
    const businessPartnerId = businessPartner.id;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [accounting, setAccounting] = useState<bpAccountingResponse | null>(null);
    const [invoices, setInvoices] = useState<BpInvoiceListItem[]>([]);
    const [filter, setFilter] = useState<DocumentKindFilter>('ALL');
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const issuer = useMemo(() => buildBpInvoiceIssuer(issuerCompany), [issuerCompany]);

    const serviceTypeLabel = useCallback(
        (code: string) => shipmentSettings?.billingLineServiceType?.find((s) => s.code === code)?.name || code,
        [shipmentSettings]
    );

    const accountNumber = useMemo(() => {
        const raw =
            businessPartner.BusinessPartnerNumber ||
            (businessPartner.bpNumber != null ? String(businessPartner.bpNumber) : businessPartner.id.slice(0, 8));
        return `AC-${raw}`;
    }, [businessPartner]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        void Promise.all([
            client.businessPartners().accounting().fetchAccounting(businessPartnerId, { range: 'all', status: 'ALL' }),
            client.businessPartners().accounting().fetchFinancialDocuments(businessPartnerId)
        ])
            .then(([accountingResult, documentsResult]) => {
                if (cancelled) return;
                setAccounting(accountingResult);
                setInvoices((documentsResult.invoices || []) as BpInvoiceListItem[]);
            })
            .catch((e) => {
                if (!cancelled) setError((e as Error)?.message || 'Failed to load financial documents');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [businessPartnerId]);

    const accountingShipmentNumberById = useMemo(() => {
        const map = new Map<string, string>();
        (accounting?.rows || []).forEach((row) => {
            if (row.shipmentId && row.shipmentNumber) {
                map.set(row.shipmentId, row.shipmentNumber);
            }
        });
        return map;
    }, [accounting?.rows]);

    const resolveInvoiceShipmentNumber = useCallback(
        (inv: BpInvoiceListItem) =>
            resolveShipmentDisplayNumber({
                cargoZoneNumber: inv.shipmentNumber,
                invoiceNumber: inv.invoiceNumber,
                accountingShipmentNumber: accountingShipmentNumberById.get(inv.shipmentId)
            }),
        [accountingShipmentNumberById]
    );

    const documents = useMemo((): FinancialDocumentItem[] => {
        const invoiceDocs: FinancialDocumentItem[] = invoices.map((inv) => {
            const shipmentNumber = resolveInvoiceShipmentNumber(inv);
            return {
                id: `invoice-${inv.id}`,
                kind: 'INVOICE',
                title: inv.invoiceNumber,
                subtitle: shipmentNumber !== '—' ? `Shipment ${shipmentNumber}` : 'Invoice',
                date: inv.invoiceDate,
                amount: inv.subtotal,
                currency: inv.currency,
                shipmentId: inv.shipmentId,
                shipmentNumber,
                invoiceId: inv.id
            };
        });

        const creditDocs: FinancialDocumentItem[] = (accounting?.ledgerEntries || [])
            .filter((entry) => entry.entryType === 'CREDIT_NOTE')
            .map((entry) => ({
                id: `credit-${entry.id}`,
                kind: 'CREDIT_NOTE',
                title: buildCreditNoteTitle(entry),
                subtitle: entry.shipmentNumber ? `Shipment ${entry.shipmentNumber}` : entry.description || 'Credit note',
                date: entry.createdAt,
                amount: Math.abs(entry.amount),
                currency: entry.currency,
                shipmentId: entry.shipmentId,
                shipmentNumber: entry.shipmentNumber || undefined,
                billingLineId: entry.billingLineId ?? undefined
            }));

        const statementDoc: FinancialDocumentItem = {
            id: STATEMENT_DOC_ID,
            kind: 'STATEMENT',
            title: 'Account Statement',
            subtitle: accountNumber,
            date: new Date().toISOString(),
            amount: accounting?.kpis.totalOpenBalance,
            currency: accounting?.kpis.currency
        };

        return [...invoiceDocs, ...creditDocs, statementDoc].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }, [invoices, accounting, accountNumber, resolveInvoiceShipmentNumber]);

    const filteredDocuments = useMemo(() => {
        const q = search.trim().toLowerCase();
        return documents.filter((doc) => {
            if (filter !== 'ALL' && doc.kind !== filter) return false;
            if (!q) return true;
            return [doc.title, doc.subtitle, doc.shipmentNumber, doc.kind].some((part) =>
                (part || '').toLowerCase().includes(q)
            );
        });
    }, [documents, filter, search]);

    useEffect(() => {
        if (!filteredDocuments.length) {
            setSelectedId(null);
            return;
        }
        if (!selectedId || !filteredDocuments.some((doc) => doc.id === selectedId)) {
            setSelectedId(filteredDocuments[0].id);
        }
    }, [filteredDocuments, selectedId]);

    const selectedDocument = useMemo(
        () => filteredDocuments.find((doc) => doc.id === selectedId) || null,
        [filteredDocuments, selectedId]
    );

    useEffect(() => {
        if (!selectedDocument || !accounting) {
            setPreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            setPreviewError(null);
            return;
        }

        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });

        const buildPreview = async () => {
            try {
                let file: File;

                if (selectedDocument.kind === 'STATEMENT') {
                    file = await getBpStatementPdfFile({
                        businessPartner,
                        accounting,
                        issuer,
                        statementDate: new Date(),
                        accountNumber
                    });
                } else if (selectedDocument.kind === 'INVOICE' && selectedDocument.invoiceId && selectedDocument.shipmentId) {
                    const resolved = await resolveBillingLinesForInvoice({
                        shipmentId: selectedDocument.shipmentId,
                        businessPartnerId: businessPartner.id,
                        invoiceId: selectedDocument.invoiceId
                    });
                    const shipment = await client.shipments().fetchById(selectedDocument.shipmentId);
                    const invoiceMeta = invoices.find((inv) => inv.id === selectedDocument.invoiceId);
                    const termsCode = businessPartner.paymentTerms;
                    file = await getBpInvoicePdfFile({
                        businessPartner,
                        shipment,
                        billingLines: resolved.billingLines,
                        issuer,
                        invoiceNumber: resolved.invoiceNumber,
                        invoiceDate: invoiceMeta?.invoiceDate ? new Date(invoiceMeta.invoiceDate) : new Date(),
                        dueDate: invoiceMeta?.dueDate ? new Date(invoiceMeta.dueDate) : null,
                        paymentTermsLabel:
                            paymentTermsLabelFromCode(termsCode) || accounting.kpis.paymentTermsLabel,
                        currency: selectedDocument.currency || accounting.kpis.currency,
                        serviceTypeLabel
                    });
                } else if (
                    selectedDocument.kind === 'CREDIT_NOTE' &&
                    selectedDocument.shipmentId &&
                    selectedDocument.billingLineId != null
                ) {
                    const allLines: billingLine[] = await client
                        .shipments()
                        .billingLines()
                        .fetchShipmentBillingLines(selectedDocument.shipmentId);
                    const line = allLines.find((l) => Number(l.id) === Number(selectedDocument.billingLineId));
                    if (!line) throw new Error('Credit note billing line not found');
                    const shipment = await client.shipments().fetchById(selectedDocument.shipmentId);
                    file = await getBpInvoicePdfFile({
                        businessPartner,
                        shipment,
                        billingLines: [line],
                        issuer,
                        invoiceNumber: selectedDocument.title,
                        invoiceDate: new Date(selectedDocument.date),
                        dueDate: null,
                        paymentTermsLabel:
                            paymentTermsLabelFromCode(businessPartner.paymentTerms) ||
                            accounting.kpis.paymentTermsLabel,
                        currency: selectedDocument.currency || accounting.kpis.currency,
                        serviceTypeLabel,
                        documentKind: 'credit_note'
                    });
                } else {
                    throw new Error('Unable to preview this document');
                }

                if (cancelled) return;
                const objectUrl = URL.createObjectURL(file);
                setPreviewUrl(objectUrl);
            } catch (e) {
                if (!cancelled) {
                    setPreviewUrl(null);
                    setPreviewError((e as Error)?.message || 'Failed to generate preview');
                }
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        };

        void buildPreview();

        return () => {
            cancelled = true;
        };
    }, [selectedDocument, accounting, businessPartner, issuer, accountNumber, invoices, serviceTypeLabel]);

    const handleDownload = () => {
        if (!previewUrl || !selectedDocument) return;
        const a = document.createElement('a');
        a.href = previewUrl;
        a.download = `${selectedDocument.title.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (loading) {
        return (
            <Centered>
                <Loader />
            </Centered>
        );
    }

    if (error) {
        return <ErrorBanner>{error}</ErrorBanner>;
    }

    return (
        <Shell>
            <Toolbar>
                <FilterTabs>
                    {FILTER_TABS.map((tab) => (
                        <FilterTab
                            key={tab.value}
                            type="button"
                            $active={filter === tab.value}
                            onClick={() => setFilter(tab.value)}
                        >
                            {tab.label}
                        </FilterTab>
                    ))}
                </FilterTabs>
                <SearchWrap>
                    <SearchOutlined />
                    <TextInput
                        value={search}
                        onChange={(v: string) => setSearch(v)}
                        placeHolder="Search documents..."
                        style={{ width: '100%' }}
                    />
                </SearchWrap>
            </Toolbar>

            <SplitLayout>
                <DocumentList>
                    {!filteredDocuments.length ? (
                        <EmptyState>No documents match your filters.</EmptyState>
                    ) : (
                        filteredDocuments.map((doc) => (
                            <DocumentCard
                                key={doc.id}
                                type="button"
                                $active={doc.id === selectedId}
                                onClick={() => setSelectedId(doc.id)}
                            >
                                <DocIcon $kind={doc.kind}>
                                    <FilePdfOutlined />
                                </DocIcon>
                                <DocMeta>
                                    <DocTitle>{doc.title}</DocTitle>
                                    <DocSubtitle>{doc.subtitle}</DocSubtitle>
                                    <DocFooter>
                                        <KindPill $kind={doc.kind}>{doc.kind.replace('_', ' ')}</KindPill>
                                        <span>{formatDate(doc.date)}</span>
                                        {doc.amount != null && doc.currency ? (
                                            <span>{formatMoney(doc.amount, doc.currency)}</span>
                                        ) : null}
                                    </DocFooter>
                                </DocMeta>
                            </DocumentCard>
                        ))
                    )}
                </DocumentList>

                <PreviewPanel>
                    {!selectedDocument ? (
                        <PreviewPlaceholder>Select a document to preview</PreviewPlaceholder>
                    ) : (
                        <>
                            <PreviewHeader>
                                <div>
                                    <PreviewTitle>{selectedDocument.title}</PreviewTitle>
                                    <PreviewSubtitle>
                                        {selectedDocument.subtitle}
                                        {selectedDocument.shipmentNumber
                                            ? ` · ${moment(selectedDocument.date).format('DD MMM YYYY')}`
                                            : ''}
                                    </PreviewSubtitle>
                                </div>
                                <DownloadButton type="button" onClick={handleDownload} disabled={!previewUrl || previewLoading}>
                                    <DownloadOutlined />
                                    Download PDF
                                </DownloadButton>
                            </PreviewHeader>
                            <PreviewBody>
                                {previewLoading ? (
                                    <Centered>
                                        <Loader />
                                    </Centered>
                                ) : previewError ? (
                                    <PreviewPlaceholder>{previewError}</PreviewPlaceholder>
                                ) : previewUrl ? (
                                    <PreviewFrame title={selectedDocument.title} src={previewUrl} />
                                ) : (
                                    <PreviewPlaceholder>No preview available</PreviewPlaceholder>
                                )}
                            </PreviewBody>
                        </>
                    )}
                </PreviewPanel>
            </SplitLayout>
        </Shell>
    );
};

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 560px;
`;

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
`;

const FilterTabs = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const FilterTab = styled.button<{ $active?: boolean }>`
    border: 1px solid ${(p) => (p.$active ? p.theme.colors.primaryBlue : '#e5e7eb')};
    background: ${(p) => (p.$active ? '#eef8fd' : '#fff')};
    color: ${(p) => (p.$active ? p.theme.colors.primaryBlue : '#4b5563')};
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
`;

const SearchWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: min(100%, 280px);
    flex: 1;
    max-width: 360px;
`;

const SplitLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
    gap: 16px;
    min-height: 520px;

    @media screen and (max-width: 960px) {
        grid-template-columns: 1fr;
    }
`;

const DocumentList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 72vh;
    overflow: auto;
    padding-right: 4px;
`;

const DocumentCard = styled.button<{ $active?: boolean }>`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    width: 100%;
    text-align: left;
    border: 1px solid ${(p) => (p.$active ? p.theme.colors.primaryBlue : '#e5e7eb')};
    background: ${(p) => (p.$active ? '#f8fcff' : '#fff')};
    border-radius: 10px;
    padding: 12px;
    cursor: pointer;
`;

const DocIcon = styled.div<{ $kind: FinancialDocumentKind }>`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: ${(p) =>
        p.$kind === 'CREDIT_NOTE' ? '#b91c1c' : p.$kind === 'STATEMENT' ? '#0369a1' : p.theme.colors.primaryBlue};
    background: ${(p) =>
        p.$kind === 'CREDIT_NOTE' ? '#fef2f2' : p.$kind === 'STATEMENT' ? '#eff6ff' : '#eef8fd'};
`;

const DocMeta = styled.div`
    flex: 1;
    min-width: 0;
`;

const DocTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: #111827;
`;

const DocSubtitle = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
`;

const DocFooter = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-top: 8px;
    font-size: 12px;
    color: #6b7280;
`;

const KindPill = styled.span<{ $kind: FinancialDocumentKind }>`
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    background: ${(p) =>
        p.$kind === 'CREDIT_NOTE' ? '#fee2e2' : p.$kind === 'STATEMENT' ? '#dbeafe' : '#e0f2fe'};
    color: ${(p) =>
        p.$kind === 'CREDIT_NOTE' ? '#b91c1c' : p.$kind === 'STATEMENT' ? '#1d4ed8' : '#0369a1'};
`;

const PreviewPanel = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    min-height: 520px;
    overflow: hidden;
`;

const PreviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
`;

const PreviewTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #111827;
`;

const PreviewSubtitle = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
`;

const DownloadButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    color: #374151;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PreviewBody = styled.div`
    flex: 1;
    min-height: 420px;
    background: #f9fafb;
`;

const PreviewFrame = styled.iframe`
    width: 100%;
    height: 100%;
    min-height: 480px;
    border: 0;
    background: #fff;
`;

const PreviewPlaceholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 420px;
    color: #6b7280;
    padding: 24px;
    text-align: center;
`;

const Centered = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
`;

const EmptyState = styled.div`
    padding: 24px;
    color: #6b7280;
    text-align: center;
`;

const ErrorBanner = styled.div`
    padding: 16px;
    border-radius: 8px;
    background: #fef2f2;
    color: #b91c1c;
`;

const mapStateToProps = (state: RootState) => ({
    issuerCompany: singleCompanySelectors.company(state),
    shipmentSettings: shipmentsSelectors.settingsByLanguage(state)()
});

export default connect(mapStateToProps)(FinancialDocumentsTab);
